import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { sha256 } from "@/utils/hash";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

type GlobalTrackingParams = {
  user_plan: string;
  user_plan_type: string;
  is_trial: boolean;
  days_since_signup: number;
  minutes_used_percent: number;
  user_id?: string;
};

type TrackingContextValue = {
  pushEvent: (eventName: string, params?: Record<string, unknown>) => void;
  setGlobalParams: (params: Partial<GlobalTrackingParams>) => void;
  hashedUserId: string;
  hashedEmail: string;
  setHashedUserId: (id: string) => void;
  setHashedEmail: (email: string) => void;
};

// ─────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────
const TrackingContext = createContext<TrackingContextValue>({
  pushEvent: () => {},
  setGlobalParams: () => {},
  hashedUserId: '',
  hashedEmail: '',
  setHashedUserId: () => {},
  setHashedEmail: () => {},
});

// ─────────────────────────────────────────────────────────────
// TrackingEnrichment — lives INSIDE AuthProvider, updates global params
// Must be placed inside AuthProvider in the component tree.
// ─────────────────────────────────────────────────────────────
export function TrackingEnrichment() {
  const { setGlobalParams, pushEvent, setHashedUserId, setHashedEmail } = useTracking();
  const trialTrackedRef = useRef<boolean>(false);
  const churnSignalTrackedRef = useRef<boolean>(false);
  const sessionStartTrackedRef = useRef<boolean>(false);

  // These hooks are always called unconditionally (rules of hooks)
  const { user } = useAuth();
  const { plan, usagePercentage } = useSubscription();
  const { isTrial, daysLeft, minutesAllocated } = useTrialStatus();

  // Calculate days_since_signup safely
  const daysSinceSignup = (() => {
    if (!user?.created_at) return 0;
    const created = new Date(user.created_at).getTime();
    if (isNaN(created)) return 0;
    return Math.floor((Date.now() - created) / 86400000);
  })();

  // ── Hash: userId ──
  useEffect(() => {
    if (!user?.id) {
      setHashedUserId('');
      return;
    }
    let cancelled = false;
    sha256(user.id).then((hash) => {
      if (!cancelled) setHashedUserId(hash);
    });
    return () => { cancelled = true; };
  }, [user?.id, setHashedUserId]);

  // ── Hash: email ──
  useEffect(() => {
    if (!user?.email) {
      setHashedEmail('');
      return;
    }
    let cancelled = false;
    sha256(user.email.toLowerCase().trim()).then((hash) => {
      if (!cancelled) setHashedEmail(hash);
    });
    return () => { cancelled = true; };
  }, [user?.email, setHashedEmail]);

  // ── Global params (including hashed user_id when available) ──
  const { hashedUserId } = useTracking();
  useEffect(() => {
    const params: Record<string, unknown> = {
      user_plan: plan?.code ?? "none",
      user_plan_type: plan?.type ?? "none",
      is_trial: isTrial ?? false,
      days_since_signup: daysSinceSignup,
      minutes_used_percent: usagePercentage ?? 0,
    };
    // Only include user_id when available (don't send empty string)
    if (hashedUserId) {
      params.user_id = hashedUserId;
    }
    setGlobalParams(params);
  }, [plan?.code, plan?.type, isTrial, daysSinceSignup, usagePercentage, hashedUserId, setGlobalParams]);

  // Track trial_started once per session
  useEffect(() => {
    // Check sessionStorage for deduplication across remounts
    if (sessionStorage.getItem("trial_start_tracked")) {
      trialTrackedRef.current = true;
    }

    if (isTrial && !trialTrackedRef.current) {
      trialTrackedRef.current = true;
      sessionStorage.setItem("trial_start_tracked", "1");
      pushEvent("trial_started", {
        trial_duration_days: daysLeft || 14,
        trial_minutes: minutesAllocated || 60,
      });
    }
  }, [isTrial, daysLeft, minutesAllocated, pushEvent]);

  // ── Tracking: churn_risk_signal ──
  // Only evaluate after subscription data has loaded (plan !== null is the proxy)
  useEffect(() => {
    // Restore deduplication from sessionStorage on remount
    if (sessionStorage.getItem("churn_signal_tracked")) {
      churnSignalTrackedRef.current = true;
    }
    if (churnSignalTrackedRef.current) return;

    // Wait until subscription data has loaded (plan !== null indicates fetch complete)
    const subscriptionLoaded = plan !== null;
    if (!subscriptionLoaded) return;

    const planCode = plan?.code || 'none';
    const usagePct = usagePercentage ?? 0;

    // Churn signal: loaded + 14+ days since signup + usage < 10% + not trial + has real plan
    const isChurnRisk =
      daysSinceSignup > 14 &&
      usagePct < 10 &&
      !isTrial &&
      planCode !== 'none' &&
      planCode !== 'FREE_100';

    if (isChurnRisk) {
      churnSignalTrackedRef.current = true;
      sessionStorage.setItem("churn_signal_tracked", "1");
      pushEvent("churn_risk_signal", {
        days_since_signup: daysSinceSignup,
        minutes_used_percent: usagePct,
        plan_code: planCode,
        risk_reason: "low_usage_after_14_days",
      });
    }
  }, [plan, usagePercentage, daysSinceSignup, isTrial, pushEvent]);

  // ── Tracking: session_start (once per browser session) ──
  // Fires on every authenticated session start, including OAuth redirect returns.
  useEffect(() => {
    // Restore deduplication from sessionStorage on remount
    if (sessionStorage.getItem("session_start_tracked")) {
      sessionStartTrackedRef.current = true;
    }
    if (sessionStartTrackedRef.current) return;

    // Only fire when authenticated AND subscription data is loaded (plan !== null)
    if (!user?.id || plan === null) return;

    sessionStartTrackedRef.current = true;
    sessionStorage.setItem("session_start_tracked", "1");

    const loginMethod = user.app_metadata?.provider === 'google' ? 'google' : 'email';
    const planCode = plan?.code || 'none';

    pushEvent("session_start", {
      login_method: loginMethod,
      plan_code: planCode,
      days_since_signup: daysSinceSignup,
      consultations_total: 0,
      is_trial: isTrial ?? false,
    });
  }, [user?.id, plan, daysSinceSignup, isTrial, pushEvent]);

  return null;
}

// ─────────────────────────────────────────────────────────────
// TrackingProvider — lives ABOVE AuthProvider
// ─────────────────────────────────────────────────────────────
export function TrackingProvider({ children }: { children: ReactNode }) {
  const [globalParams, setGlobalParamsState] = useState<GlobalTrackingParams>({
    user_plan: "none",
    user_plan_type: "none",
    is_trial: false,
    days_since_signup: 0,
    minutes_used_percent: 0,
  });

  const [hashedUserId, setHashedUserId] = useState<string>('');
  const [hashedEmail, setHashedEmail] = useState<string>('');

  // Initialize dataLayer on mount
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
  }, []);

  const setGlobalParams = useCallback((params: Partial<GlobalTrackingParams>) => {
    setGlobalParamsState((prev) => ({ ...prev, ...params }));
  }, []);

  const pushEvent = useCallback(
    (eventName: string, params: Record<string, unknown> = {}) => {
      window.dataLayer = window.dataLayer || [];
      const payload = {
        event: eventName,
        ...globalParams,
        ...params,
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
        ts: Date.now(),
      };
      window.dataLayer.push(payload);

      if (import.meta.env.DEV) {
        console.log("[Tracking]", eventName, params);
      }
    },
    [globalParams]
  );

  const value = useMemo(
    () => ({ pushEvent, setGlobalParams, hashedUserId, hashedEmail, setHashedUserId, setHashedEmail }),
    [pushEvent, setGlobalParams, hashedUserId, hashedEmail]
  );

  return (
    <TrackingContext.Provider value={value}>
      <PageViewTracker pushEvent={pushEvent} globalParams={globalParams} />
      {children}
    </TrackingContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────
// PageViewTracker — handles page_view events via location
// ─────────────────────────────────────────────────────────────
function PageViewTracker({
  pushEvent,
  globalParams,
}: {
  pushEvent: (name: string, params?: Record<string, unknown>) => void;
  globalParams: GlobalTrackingParams;
}) {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    if (lastPathRef.current === currentPath) return;
    lastPathRef.current = currentPath;

    const timeoutId = setTimeout(() => {
      pushEvent("page_view", {
        page_path: currentPath,
        page_location: window.location.href,
        page_title: document.title,
        logged_in: globalParams.user_plan !== "none",
      });
    }, 100);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return null;
}

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────
export function useTracking() {
  return useContext(TrackingContext);
}
